import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Spinner,
  Table,
} from "react-bootstrap";
import { useGetLazy, usePost } from "../../api";
import { EmptyState, FilterBar } from "../../components/ui";
import {
  IOpenDataReadinessRunsFilter,
  IOpenDataReadinessRunsResponse,
  OpenDataReadinessRunStatus,
} from "../../interfaces";
import { formatDuration } from "./readiness-common";

const RUN_STATUS_VARIANT: Record<OpenDataReadinessRunStatus, string> = {
  pending: "secondary",
  running: "primary",
  completed: "success",
  failed: "danger",
  cancelled: "dark",
};

const emptyFilter: IOpenDataReadinessRunsFilter = {
  status: "",
  type: "",
  triggeredBy: "",
  snapshotDate: "",
};

export const OpenDataReadinessRuns: React.FC = () => {
  const [draft, setDraft] = useState(emptyFilter);
  const [applied, setApplied] = useState(emptyFilter);
  const [notice, setNotice] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState<string | null>(null);

  const { data, loading, error, execute } =
    useGetLazy<IOpenDataReadinessRunsResponse>();
  const { execute: post } = usePost<unknown, unknown>();

  // Held in a ref for the same reason as the merchants tab: `execute` changes
  // identity on an auth-context render and would restart the poll below.
  const executeRef = useRef(execute);
  executeRef.current = execute;

  const load = useCallback((filter: IOpenDataReadinessRunsFilter) => {
    const params = new URLSearchParams();
    Object.entries(filter).forEach(([k, v]) => {
      if (v) params.set(k, String(v));
    });
    executeRef
      .current(`open-data-readiness/runs?${params}`)
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    load(applied);
  }, [applied, load]);

  // A sweep takes minutes, so the page refreshes itself while one is live.
  useEffect(() => {
    if (!data?.activeCount) return;
    const timer = setInterval(() => load(applied), 10_000);
    return () => clearInterval(timer);
  }, [data?.activeCount, applied, load]);

  const cancel = async (runId: string) => {
    setCancelling(runId);
    try {
      await post(`open-data-readiness/runs/${runId}/cancel`, {});
      setNotice(`Run ${runId} cancelled.`);
      load(applied);
    } catch {
      setNotice("Could not cancel that run.");
    } finally {
      setCancelling(null);
    }
  };

  const activeCount = Object.values(applied).filter(Boolean).length;

  return (
    <div>
      {notice && (
        <Alert variant="info" dismissible onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}
      {error && <Alert variant="danger">Could not load runs.</Alert>}

      <FilterBar
        activeCount={activeCount}
        defaultOpen={false}
        onSearch={() => setApplied(draft)}
        onClear={() => {
          setDraft(emptyFilter);
          setApplied(emptyFilter);
        }}
        summary={
          data
            ? `${data.total} run${data.total === 1 ? "" : "s"}${data.activeCount ? ` · ${data.activeCount} active` : ""}`
            : undefined
        }
      >
        <Col md={3}>
          <Form.Label className="small fw-bold">Status</Form.Label>
          <Form.Select
            value={draft.status ?? ""}
            onChange={(e) => setDraft((d) => ({ ...d, status: e.target.value }))}
          >
            <option value="">Any</option>
            {Object.keys(RUN_STATUS_VARIANT).map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={3}>
          <Form.Label className="small fw-bold">Type</Form.Label>
          <Form.Select
            value={draft.type ?? ""}
            onChange={(e) => setDraft((d) => ({ ...d, type: e.target.value }))}
          >
            <option value="">Any</option>
            <option value="bulk">Bulk sweep</option>
            <option value="single">Single item</option>
          </Form.Select>
        </Col>
        <Col md={3}>
          <Form.Label className="small fw-bold">Date</Form.Label>
          <Form.Select
            value={draft.snapshotDate ?? ""}
            onChange={(e) =>
              setDraft((d) => ({ ...d, snapshotDate: e.target.value }))
            }
          >
            <option value="">Latest</option>
            <option value="all">All dates</option>
            {(data?.availableDates ?? []).map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={3}>
          <Form.Label className="small fw-bold">Triggered by</Form.Label>
          <Form.Select
            value={draft.triggeredBy ?? ""}
            onChange={(e) =>
              setDraft((d) => ({ ...d, triggeredBy: e.target.value }))
            }
          >
            <option value="">Anyone</option>
            {(data?.triggeredByOptions ?? []).map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </Form.Select>
        </Col>
      </FilterBar>

      <Card>
        <Card.Body className="p-0">
          {loading && !data ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
            </div>
          ) : !data || data.runs.length === 0 ? (
            <EmptyState
              title="No runs yet"
              message="Start one from the Run sweep button above."
            />
          ) : (
            <Table hover responsive size="sm" className="mb-0 align-middle">
              <thead>
                <tr>
                  <th>Started</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Triggered by</th>
                  <th className="text-end">Items</th>
                  <th className="text-end">API calls</th>
                  <th className="text-end">Batches</th>
                  <th>Duration</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.runs.map((r) => (
                  <tr key={r.runId}>
                    <td className="small">
                      {new Date(r.startedAt).toLocaleString()}
                      <div className="text-muted" style={{ fontSize: "0.72rem" }}>
                        {r.snapshotDate}
                      </div>
                    </td>
                    <td>
                      <Badge bg="light" text="dark">
                        {r.type}
                      </Badge>
                    </td>
                    <td>
                      <Badge bg={RUN_STATUS_VARIANT[r.status]}>{r.status}</Badge>
                      {r.errorMessage && (
                        <div className="text-danger small">{r.errorMessage}</div>
                      )}
                    </td>
                    <td className="small">{r.triggeredBy ?? "—"}</td>
                    <td className="text-end small">
                      {r.itemsProcessed}/{r.itemsConsidered}
                      {r.itemsFailed > 0 && (
                        <span className="text-danger"> · {r.itemsFailed} failed</span>
                      )}
                    </td>
                    <td className="text-end small">{r.apiCalls}</td>
                    <td className="text-end small">
                      {r.completedBatches}/{r.totalBatches}
                    </td>
                    <td className="small">{formatDuration(r.durationMs)}</td>
                    <td className="text-end">
                      {["pending", "running"].includes(r.status) && (
                        <Button
                          size="sm"
                          variant="outline-danger"
                          disabled={cancelling === r.runId}
                          onClick={() => cancel(r.runId)}
                          title="Stop this run — batches already finished keep their snapshots"
                        >
                          {cancelling === r.runId ? (
                            <Spinner size="sm" animation="border" />
                          ) : (
                            "Cancel"
                          )}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};
